import {
  DISCOVERY_LOCATION_FILTERS,
  DISCOVERY_LOCATION_LABELS,
  type DiscoveryLocationFilter,
  DISCOVERY_QUERY_PRESETS,
  type DiscoveryProviderDTO,
  type DiscoverySearchDTO,
  type DiscoverySearchStatus,
} from '@hearth/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Compass, Search } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { RelativeTime } from '../../components/crm';
import { Page } from '../../components/layout/Page';
import { PageHeader } from '../../components/layout/PageHeader';
import { Badge, Button, Card, CardBody, CardHeader, EmptyState, Field, Input, Select, useToast } from '../../components/ui';
import { api, errorMessage } from '../../lib/api';

export const SEARCH_STATUS_COLORS: Record<DiscoverySearchStatus, string> = {
  queued: 'slate',
  running: 'sky',
  completed: 'green',
  failed: 'red',
};

/**
 * Find pizzerias from a public data source. Results land in a review queue — nothing enters the CRM until a
 * salesperson imports or merges it.
 */
export function DiscoveryPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { data: providers } = useQuery({
    queryKey: ['discovery', 'providers'],
    queryFn: ({ signal }) => api.get<{ items: DiscoveryProviderDTO[] }>('/api/discovery/providers', { signal }),
  });
  const { data: searches } = useQuery({
    queryKey: ['discovery', 'searches'],
    queryFn: ({ signal }) => api.get<{ items: DiscoverySearchDTO[] }>('/api/discovery/searches', { signal }),
    refetchInterval: (query) => (query.state.data?.items.some((s) => s.status === 'queued' || s.status === 'running') ? 3_000 : false),
  });

  const [providerKey, setProviderKey] = useState('');
  const [presetKey, setPresetKey] = useState<string>(DISCOVERY_QUERY_PRESETS[0].key);
  const [locationText, setLocationText] = useState('');
  const [radiusKm, setRadiusKm] = useState('10');
  const [keywords, setKeywords] = useState('');
  const [locations, setLocations] = useState<DiscoveryLocationFilter>(DISCOVERY_QUERY_PRESETS[0].locations);

  /** A preset carries both the cuisine and the operator size; picking one sets both, then either can be tweaked. */
  const choosePreset = (key: string) => {
    setPresetKey(key);
    const chosen = DISCOVERY_QUERY_PRESETS.find((p) => p.key === key);
    if (!chosen) return;
    setLocations(chosen.locations);
    if (chosen.locations !== 'any' && chosen.locations !== 'single') setRadiusKm((current) => (Number(current) < 50 ? '50' : current));
  };

  const items = providers?.items ?? [];
  const provider = items.find((p) => p.key === providerKey) ?? items.find((p) => p.configured) ?? items[0];
  const preset = DISCOVERY_QUERY_PRESETS.find((p) => p.key === presetKey) ?? DISCOVERY_QUERY_PRESETS[0];

  const start = useMutation({
    mutationFn: () =>
      api.post<DiscoverySearchDTO>('/api/discovery/searches', {
        provider: provider!.key,
        query: preset.query,
        keywords: [preset.keywords, keywords].filter(Boolean).join(', ') || null,
        locations,
        locationText,
        radiusKm: Number(radiusKm),
      }),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: ['discovery', 'searches'] });
      navigate(`/discover/searches/${created.id}`);
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  return (
    <Page width="wide">
      <PageHeader title="Discover pizzerias" description="Search public listings for pizzerias you don't know yet, then review before anything enters the CRM." />
      <div className="grid gap-4 lg:grid-cols-[minmax(0,26rem)_minmax(0,1fr)]">
        <Card className="self-start">
          <CardHeader title="New search" />
          <CardBody>
            <form
              className="space-y-3"
              onSubmit={(event) => {
                event.preventDefault();
                start.mutate();
              }}
            >
              <Field label="Data source">
                <Select value={provider?.key ?? ''} onChange={(event) => setProviderKey(event.target.value)}>
                  {items.map((p) => (
                    <option key={p.key} value={p.key}>
                      {p.label}
                      {p.configured ? '' : ' (not set up)'}
                    </option>
                  ))}
                </Select>
              </Field>
              {provider && !provider.configured && (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-[13px] text-amber-900">
                  <p className="font-medium">{provider.label} is not set up yet</p>
                  <p className="mt-1">{provider.setup ?? 'Ask your administrator to configure this data source.'}</p>
                </div>
              )}
              <Field label="Looking for">
                <Select value={presetKey} onChange={(event) => choosePreset(event.target.value)}>
                  {DISCOVERY_QUERY_PRESETS.map((p) => (
                    <option key={p.key} value={p.key}>
                      {p.label}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Where" required>
                <Input value={locationText} onChange={(event) => setLocationText(event.target.value)} placeholder="Montréal, QC" required />
              </Field>
              <div className="grid grid-cols-2 gap-3">

                <Field label="How many locations" hint="Counted from the branches found in this search.">
                  <Select value={locations} onChange={(event) => setLocations(event.target.value as DiscoveryLocationFilter)}>
                    {DISCOVERY_LOCATION_FILTERS.map((option) => (
                      <option key={option} value={option}>
                        {DISCOVERY_LOCATION_LABELS[option]}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field label="Radius (km)" hint={locations === 'chain' || locations === 'small' || locations === 'mid' ? 'Wider finds more branches of the same chain.' : undefined}>
                  <Input type="number" min={1} max={100} value={radiusKm} onChange={(event) => setRadiusKm(event.target.value)} />
                </Field>
                <Field label="Extra keywords" hint="Comma-separated">
                  <Input value={keywords} onChange={(event) => setKeywords(event.target.value)} placeholder="deck oven" />
                </Field>
              </div>
              <Button type="submit" variant="primary" icon={Search} loading={start.isPending} disabled={!provider?.configured || locationText.trim() === ''}>
                Search
              </Button>
              {provider && <p className="text-xs text-slate-500">{provider.terms}</p>}
            </form>
          </CardBody>
        </Card>

        <Card className="self-start">
          <CardHeader title="Recent searches" />
          {(searches?.items.length ?? 0) === 0 ? (
            <EmptyState icon={Compass} title="No searches yet" description="Try “Wood-fired pizza” around a city you cover." />
          ) : (
            <ul className="divide-y divide-slate-100">
              {searches!.items.map((search) => (
                <li key={search.id} className="flex flex-wrap items-center gap-2 px-4 py-3 text-[13px]">
                  <Link to={`/discover/searches/${search.id}`} className="font-medium text-slate-900 hover:text-ember-700">
                    {search.query} — {search.locationText ?? `${search.latitude?.toFixed(3)}, ${search.longitude?.toFixed(3)}`}
                  </Link>
                  <Badge color={SEARCH_STATUS_COLORS[search.status]} size="sm">
                    {search.status}
                  </Badge>
                  <span className="text-slate-500">{search.providerLabel}</span>
                  <span className="ml-auto text-slate-600">
                    {search.resultCount} found · {search.newCount} new · {search.pendingCount} to review
                  </span>
                  <RelativeTime value={search.createdAt} className="text-xs text-slate-400" />
                  {search.error && <span className="basis-full text-xs text-red-700">{search.error}</span>}
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </Page>
  );
}
